import React, { useEffect, useState } from 'react';
import FileUploadInput from '../../Shared/Components/FileUpload/FileUploadInput';
import { Box, Button, Paper, Typography, IconButton } from '@mui/material';
import Status from '../../Shared/Components/Status/Status';
import { UploadImagesFormProps } from '../Types/hotelTypes';
import { useUploadHotelImagesMutation } from '../Service/extendedHotelsApi';
import DeleteIcon from '@mui/icons-material/Delete';

const UploadImagesForm = ({ hotelId, onUploadSuccess }: UploadImagesFormProps) => {
    const [files, setFiles] = useState<File[]>([]);
    const [previews, setPreviews] = useState<string[]>([]);
    const [uploadHotelImages, { isLoading, isSuccess, isError, error }] = useUploadHotelImagesMutation();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const selectedFiles = Array.from(e.target.files);
            setFiles((prev) => [...prev, ...selectedFiles]);
            const newPreviews = selectedFiles.map((file) => URL.createObjectURL(file));
            setPreviews((prev) => [...prev, ...newPreviews]);
        }
    };

    const handleRemove = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setPreviews((prev) => prev.filter((_, i) => i !== index));
    };

    const handleUploadClick = async () => {
        if (!files.length) {
            console.warn('Brak plików do wysłania');
            return;
        }

        try {
          await uploadHotelImages({ files, hotelId }).unwrap();
          setFiles([]);
          setPreviews([]);
            onUploadSuccess?.();
        } catch (err) {
            console.error('Błąd uploadu zdjęć:', err);
        }
    };

    useEffect(() => {
        return () => {
            previews.forEach((preview) => URL.revokeObjectURL(preview));
        };
    }, [previews]);

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
                Możesz jednorazowo wgrać do <strong>5 zdjęć</strong> (każde maks. <strong>2MB</strong>)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Wybrano <strong>{files.length}</strong> z 5 możliwych
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FileUploadInput onFilesSelected={(files) => handleFileChange({ target: { files } } as any)} />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUploadClick}
                    disabled={isLoading || !files.length}
                    size="large"
                >
                    {isLoading ? 'Wysyłanie...' : 'Wyślij zdjęcia'}
                </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}>
                {previews.map((src, index) => (
                  <Box
                      key={index}
                      sx={{
                          m: 1,
                          position: 'relative',
                          width: 100,
                          height: 100,
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: '0 0 4px rgba(0,0,0,0.2)',
                      }}
                  >
                      <img
                          src={src}
                          alt={`preview-${index}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <IconButton
                          size="small"
                          onClick={() => handleRemove(index)}
                          sx={{
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              backgroundColor: 'rgba(255,255,255,0.7)',
                              '&:hover': { backgroundColor: 'rgba(255,255,255)' },
                          }}
                      >
                          <DeleteIcon fontSize="small" />
                      </IconButton>
                  </Box>
              ))}
            </Box>

            <Status isLoading={isLoading} isError={isError} isSuccess={isSuccess} error={error} />
        </Paper>
    );
};

export default UploadImagesForm;
